import { useIntl } from "react-intl";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  ContentButtonNavigation,
  navButtonTypes,
} from "~/hec/components/ui/carousel";
import { Heading } from "~/hec/components/ui/heading";
import type { TransformedLevel } from "~/hooks/learning-center/transformers";

import messages from "./messages";
import ModuleCard from "./module-card";

/**
 * SFCC-11504 — port of hec-composable `learning-center-levels/level.jsx`.
 * One level: a heading ("Level N: title") above a carousel of `ModuleCard`s
 * (4-up desktop, 1-up mobile).
 *
 * Delta vs composable: carousel controls (arrows + dots) are gated with
 * CSS breakpoint classes instead of `useMediaQuery`. The module count is known
 * at SSR, so this avoids the hook's documented hydration/CLS flash (see
 * `useMediaQuery.ts`). Controls show only when the slides don't all fit:
 * on mobile when >1 module, on desktop when >4.
 */

interface LevelProps {
  levelData: TransformedLevel;
  goalID: string;
}

const Level = ({ levelData, goalID }: LevelProps) => {
  const intl = useIntl();
  const { levelID, levelNumber, title, modules, started } = levelData;
  const moduleCount = modules.length;

  // Empty-modules guard (SFCC-11724): a level whose Builder tree resolved no
  // modules would otherwise render a heading above an empty carousel. Skip it —
  // the parent (LearningCenterLevels) already filters these out, this is
  // defense-in-depth for any direct caller.
  if (moduleCount === 0) return null;

  // CSS-only control visibility: mobile shows 1 slide (controls when >1),
  // desktop shows 4 (controls when >4). `lg:hidden` hides the desktop controls
  // for the 2–4 range where everything already fits at lg.
  const showControls = moduleCount > 1;
  const controlsClass = moduleCount > 4 ? undefined : "lg:hidden";

  return (
    <div data-testid="lms-level">
      <Heading
        tag="h3"
        variant="h3"
        className="text-center md:text-left"
        data-testid="lms-level-heading"
      >
        {intl.formatMessage(messages.levelHeading, { levelNumber, title })}
      </Heading>
      <Carousel opts={{ loop: true }} data-testid="lms-level-carousel">
        {showControls && (
          <CarouselPrevious
            overlapContent
            className={controlsClass}
            data-testid="lms-level-prev"
          />
        )}
        <CarouselContent
          className="mx-0 mt-4 lg:-mx-3"
          data-testid="lms-level-carousel-content"
        >
          {modules.map((module, moduleIndex) => (
            <CarouselItem
              key={module.moduleID ?? moduleIndex}
              slidesVisible="4"
              slidesVisibleMobile="1"
              slidePadding="3"
              slidePaddingMobile="0"
              data-testid="lms-level-carousel-item"
            >
              <div className="flex h-full justify-center">
                <div className="h-full w-[17.9375rem] lg:w-full">
                  <ModuleCard
                    moduleData={module}
                    levelID={levelID}
                    levelStarted={started}
                    goalID={goalID}
                  />
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {showControls && (
          <CarouselNext
            overlapContent
            className={controlsClass}
            data-testid="lms-level-next"
          />
        )}
        {showControls && (
          <ContentButtonNavigation
            buttonType={navButtonTypes.FlatButton}
            className={controlsClass}
            data-testid="lms-level-dots"
          />
        )}
      </Carousel>
    </div>
  );
};

export default Level;
